// Totaux de tomes par série + détection des tomes manquants.
// Partagé entre la vue Séries, la vue Manquants, les stats et le partage.
//
// Les totaux (tomes numérotés + hors-séries) sont gardés en local (rapide, marche
// hors-ligne) ET synchronisés dans le compte Firebase de l'utilisateur
// (users/{uid}/settings/series) : ils suivent donc l'appli d'un appareil à l'autre
// et ne se perdent plus si le cache du navigateur est vidé.
import { useEffect, useState } from 'react';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../firebase';

const STORAGE_KEY = 'bulletheque_series_totals';
const HS_KEY = 'bulletheque_series_hs_totals';
const EVT = 'bulletheque-series-changed';

const readLS = (k) => { try { return JSON.parse(localStorage.getItem(k) || '{}'); } catch { return {}; } };
const writeLS = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} };
const notify = () => { try { window.dispatchEvent(new Event(EVT)); } catch {} };
const same = (a, b) => JSON.stringify(a || {}) === JSON.stringify(b || {});

// ── Synchronisation Firestore ────────────────────────────────────────────────
let uid = null, unsub = null, synced = false, timer = null;
let remote = { totals: {}, hs: {} };
const settingsRef = () => doc(db, 'users', uid, 'settings', 'series');

function schedulePush() {
  if (!uid || !synced) return;              // pas avant la 1re lecture : on n'écrase jamais le cloud avec du vide
  clearTimeout(timer);
  timer = setTimeout(async () => {
    const totals = readLS(STORAGE_KEY), hs = readLS(HS_KEY);
    if (same(totals, remote.totals) && same(hs, remote.hs)) return;
    try {
      await setDoc(settingsRef(), { totals, hs, updatedAt: Date.now() });
      remote = { totals, hs };
    } catch { /* hors-ligne / refusé : on réessaiera à la prochaine modification */ }
  }, 700);
}

export function startSeriesSync(userId) {
  stopSeriesSync();
  uid = userId; synced = false;
  unsub = onSnapshot(settingsRef(), (snap) => {
    if (snap.metadata.hasPendingWrites) return;   // écho de notre propre écriture
    const d = snap.exists() ? snap.data() : null;
    if (!synced) {
      synced = true;
      const lt = readLS(STORAGE_KEY), lh = readLS(HS_KEY);
      if (d) {
        remote = { totals: d.totals || {}, hs: d.hs || {} };
        // Le cloud gagne ; on garde en plus ce qui n'existe que sur cet appareil (1re synchro)
        const mt = { ...lt, ...remote.totals }, mh = { ...lh, ...remote.hs };
        writeLS(STORAGE_KEY, mt); writeLS(HS_KEY, mh); notify();
        if (!same(mt, remote.totals) || !same(mh, remote.hs)) schedulePush();
      } else {
        remote = { totals: {}, hs: {} };
        if (Object.keys(lt).length || Object.keys(lh).length) schedulePush();   // migration des totaux déjà saisis
      }
      return;
    }
    if (d) {                                       // modification venue d'un autre appareil
      remote = { totals: d.totals || {}, hs: d.hs || {} };
      writeLS(STORAGE_KEY, remote.totals); writeLS(HS_KEY, remote.hs); notify();
    }
  }, () => { /* règles / réseau : on reste en mode local */ });
}

export function stopSeriesSync() {
  if (unsub) { unsub(); unsub = null; }
  clearTimeout(timer); uid = null; synced = false;
}

// Re-render quand les totaux changent (synchro depuis le cloud, restauration…)
export function useSeriesSettings() {
  const [v, setV] = useState(0);
  useEffect(() => {
    const h = () => setV(x => x + 1);
    window.addEventListener(EVT, h);
    return () => window.removeEventListener(EVT, h);
  }, []);
  return v;
}

// Restauration depuis un fichier de sauvegarde
export function applySeriesBackup(series) {
  if (!series) return;
  writeLS(STORAGE_KEY, { ...readLS(STORAGE_KEY), ...(series.totals || {}) });
  writeLS(HS_KEY, { ...readLS(HS_KEY), ...(series.hs || {}) });
  notify(); schedulePush();
}

export const loadTotals = () => readLS(STORAGE_KEY);
export const saveTotals = t => { writeLS(STORAGE_KEY, t); schedulePush(); };
export const loadHsTotals = () => readLS(HS_KEY);
export const saveHsTotals = t => { writeLS(HS_KEY, t); schedulePush(); };

export function findMissingTomes(bds, total) {
  const set = new Set(bds.map(b => parseInt(b.numero)).filter(n => !isNaN(n) && n > 0));
  if (set.size === 0) return [];
  const max = total || Math.max(...set);
  const missing = [];
  for (let i = 1; i <= max; i++) if (!set.has(i)) missing.push(i);
  return missing;
}

// Numéros possédés en plusieurs exemplaires (utile pour repérer un doublon précis)
export function findDuplicateTomes(bds) {
  const counts = {};
  bds.forEach(b => {
    const n = parseInt(b.numero);
    if (!isNaN(n) && n > 0) counts[n] = (counts[n] || 0) + 1;
  });
  return Object.entries(counts).filter(([,c]) => c > 1).map(([n]) => parseInt(n)).sort((a,b)=>a-b);
}

// [3,4,5,8,12] → "3–5, 8, 12"
export function formatRanges(nums) {
  const out = [];
  let i = 0;
  while (i < nums.length) {
    let j = i;
    while (j + 1 < nums.length && nums[j + 1] === nums[j] + 1) j++;
    out.push(j === i ? `${nums[i]}` : j === i + 1 ? `${nums[i]}, ${nums[j]}` : `${nums[i]}–${nums[j]}`);
    i = j + 1;
  }
  return out.join(', ');
}
